import {
  BodyType,
  Breed,
  LifeStyle,
} from "libs/cat-calculations/src/lib/cat-calculations.types";
import { Gender } from "@./cat-calculations";
import { AddressValue, CancellationReason, CatLifeStage } from "@forms/schema";
import { Dayjs } from "dayjs";
import { TokenSuccessResponse } from "@auth/types";

import { IDiscount } from "@/discounts-utils";

export type Lifestyle = Exclude<LifeStyle, "UNKNOWN">;

export type LegacyRequestTypes =
  IPossibleDeliveryDates
  | ICheckoutCat
  | ILitterCheckoutRequest
  | ILitterSignUpPreference;

interface IPossibleDeliveryDate {
  date: Dayjs; // Of the format "2022-07-14"
  price: number;
}

export interface IPossibleDeliveryDates {
  dates: IPossibleDeliveryDate[];
}

export interface ICheckoutCat {
  name?: string;
  gender?: Gender;
}

export type AddOnPlanType = "HEALTH_SCOOP_LITTER" | "PLANET_SCOOP_LITTER";
export type FreshMealPlanType = "FRESH" | "FRESH_TRIAL" | "FRESH_CROSS_SELL";

export interface INewLitterCheckoutRequest {
  discountCode?: string;
  numberOfCats?: number;
  deliveryDate?: string;
  quantity?: number;
  cadence?: number;
  catIds?: string[];
}

export interface ILitterCheckoutRequest {
  discountCode?: string;
  numberOfCats?: number;
  deliveryDate?: string;
  quantity?: number;
  cadence?: number;
  cats?: string[] | null;
  formUsed?: string;
  recurringQuantity?: number;
  recurringCadence?: number;
}

export interface INewCustomerLitterCheckoutRequest {
  deliveryDate: string;
  productQuantity: number;
  cadenceWeeks: number;
  email: string;
  customerId: string;
  discountCode?: string;
  catIds: string[];
  formUsed: string;
  addonPlanType: AddOnPlanType;
  googleClientId?: string;
  postcode: string;
  shippingAddress: ShippingAddress;
  billingAddress: BillingAddress;
  fbclid?: string;
}

export type CheckoutStatus = "IN_PROGRESS" | "FAILED" | "SUCCESS" | "TIMED_OUT";
export interface ShippingAddress {
  firstName: string;
  lastName: string;
  address1: string;
  address2?: string;
  city: string;
  country?: string;
  phone?: string;
}

export interface BillingAddress {
  firstName: string;
  lastName: string;
  postcode: string;
  address1: string;
  address2?: string;
  city: string;
  country: string;
  phone?: string;
}

export interface ILitterPlanSignUpOption {
  quantity: number; // Number of bags
  cadence: number; // Number of recurring days
  pricing: {
    planQuantityDiscount: number;
    planPreDiscount: number;
    delivery: number;
    planPostDiscount: number;
    discountCode?: IDiscount | null; // Discount
    discount?: IDiscount | null; // Discount
    total: number;
    planQuantityDiscountPerItem: number;
    planPostDiscountPerItem: number;
  }
}

export const EXTRA_PRODUCT = {
  HEALTH_SCOOP_LITTER: "Scoop Health",
  PLANET_SCOOP_LITTER: "Scoop Planet",
};
export interface ILitterOptionsResponse {
  type: AddOnPlanType;
  options: ILitterPlanSignUpOption[];
}

export interface Cat {
  name: string;
  catId: string;
}

export interface Cats {
  cat: Cat;
}

export interface Customer {
  cats: { [ key: string ]: Cats };
  customerId: string;
  rechargeCustomerId: string;
  addressId: string;
}
export interface ILitterSignUpPreference {
  quantity?: string;
  discountCode?: string;
  deliveryDate?: string; // Of the format "2022-07-14"
}

export interface IBreederSignUpTrialistRequest {
  firstName: string;
  lastName: string;
  email: string;
  postcode: string;
  catData: GccfCatCheckoutDetails[];
  deliveryDate: string; // YYYY-MM-DD
  billingAddress: BillingAddress;
  shippingAddress: ShippingAddress;
  formUsed: string;
  catsInHousehold: number;
}

export interface IBreederSignUpTrialistResponse {
  katkinCustomerId: string;
  error?: string;
  message?:string;
}

export interface ICustomerExistsRequest {
  email: string;
}

export interface ICustomerExistsResponse {
  doesCustomerExist: boolean;
}

export interface ICreateCustomerResponse {
  customerId: string;
  catIds: { [ key: string ]: string }; // catName: catId for some reason...
  authData: TokenSuccessResponse;
  gtmData: unknown;
}

export type TrialType = "TWENTY_OFF" | "TWENTY_OFF_CROSS_SELL" | "GOOGLE_SHOPPING" | "NOT_A_TRIAL" | "HELLO_FRESH" | "TRY_5" |
"CL_5" |
"FOOD_5" |
"MF_5" |
"BD_5" |
"HG_5" |
"PACT_5" |
"G_5" |
"CO_5" |
"HOUSE_5" |
"NT_5" |
"COOK_5" |
"HF_5" |
"BAD_5";

export interface INewFreshCheckoutRequest {
  deliveryDate: string; // YYYY-MM-DD
  email: string;
  customerId: string;
  discountCode: string;
  catData: CatCheckoutDetails[];
  catsInHousehold: number;
  formUsed: string;
  trialType: TrialType;
  postcode: string;
  billingAddress?: BillingAddress;
  shippingAddress?: ShippingAddress;
  fbclid?: string;
  flowTrialType?: TrialType;
  discountNibbles?: string;
}

export interface INewFreshCheckoutWithoutAddressRequest extends
  INewFreshCheckoutWithoutShippingAddressRequest, INewFreshCheckoutWithoutBillingAddressRequest {
  address: AddressValue;
  firstName: string;
  lastName: string;
}

type INewFreshCheckoutWithoutShippingAddressRequest = Omit<INewFreshCheckoutRequest, "shippingAddress">;
export type INewFreshCheckoutWithoutBillingAddressRequest = Omit<INewFreshCheckoutRequest, "billingAddress">;

export interface IGccfFreshCheckoutWithoutAddressRequest extends
  IGccfFreshCheckoutWithoutShippingAddressRequest, IGccfFreshCheckoutWithoutBillingAddressRequest {
  address: AddressValue;
  firstName: string;
  lastName: string;
}

type IGccfFreshCheckoutWithoutShippingAddressRequest = Omit<IBreederSignUpTrialistRequest, "shippingAddress">;
type IGccfFreshCheckoutWithoutBillingAddressRequest = Omit<IBreederSignUpTrialistRequest, "billingAddress">;

type TFreshBelief = 1 | 2 | 3 | 4;

export interface ICreateFreshCustomer {
  firstName: string;
  lastName: string;
  email: string;
  postcode?: string;
  cats: CatCreationFresh[];
  optInReason?: string | null;
  catCountInHousehold: number;
  trialType: TrialType;
  fbclid?: string;
  fbp?: string;
  discountCode?: string;
  deliveryDate?: string; // YYYY-MM-DD
  billingAddress?: BillingAddress;
  shippingAddress?: ShippingAddress;
  customerId?: string;
  freshBelief?: TFreshBelief;
  catTreats?: string;
  switchReasons?: string[];
  flowTrialType?: TrialType;
  formName?: string;
}

export interface ICreateInfluencerCustomer extends ICreateFreshCustomer {
  cats: InfluencerCheckoutDetails[];
}

export interface CatCreationFresh {
  name: string;
  birthday: string; // YYYY-MM-DD
  bodyType: BodyType;
  brand: string | undefined;
  breed: Breed;
  benefits: string[] | undefined;
  feed: string | undefined;
  fussiness: string | undefined;
  gender: Gender;
  isBirthdayPrecise: boolean | undefined;
  isNeutered: boolean | undefined;
  lifestyle: Lifestyle;
  medicalConditions: string[] | undefined;
  medicalConditionsDetails: string | undefined;
  weight: string;
  id: string | undefined;
}

export interface GccfCatCheckoutDetails extends CatCreationFresh {
  kittenGccfNumber: number;
  recipeTitleToQuantity: Record<string, number>;
}

export interface InfluencerCheckoutDetails extends CatCreationFresh {
  recipeTitleToQuantity: Record<string, number>;
}

export interface CatCheckoutDetails {
  catName: string;
  birthday: string; // YYYY-MM-DD
  bodyType: BodyType;
  brand: string | undefined;
  breed: Breed;
  benefits: string[] | undefined;
  feedList: string[] | undefined;
  fussiness: string | undefined;
  gender: Gender;
  isBirthdayPrecise: boolean | undefined;
  isNeutered: boolean | undefined;
  lifestyle: Lifestyle;
  medicalConditions: string[] | undefined;
  allergyList: string[] | undefined;
  medicalNotes: string | undefined;
  weight: string;
  id: string | undefined;
  nibblesChickenQuantity?: number,
  nibblesSalmonQuantity?: number,
  sprinklesQuantity?: number,
  recipeTitleToQuantity: Record<string, number>

  catLifeStage?: CatLifeStage;
  catAgeYears?: number;
  catAgeMonths?: number;
  catAgeWeeks?: number;
  catHasAllergies?: boolean;
  catHasMedicalConditions?: boolean;
  catPlanRemoved?: boolean;
}

/** @deprecated Use FreshRecipe instead */
export type RecipeProductTitle = "BAAA" | "CLUCK" | "MOOO" | "SPLASH" | "GOBBLE" | "QUACK" | "OINK";

/** @deprecated Use FreshExtraProduct instead */
export type ExtraProductTitle = "SPRINKLES" | "NIBBLES_CHICKEN" | "NIBBLES_SALMON";

export interface ICancelSubscriptionRequest {
  rechargeSubscriptionId: number;
  reason: CancellationReasonData;
  survey: SurveyResult;
}

export interface NpsCategoryScoring {
  quality?: boolean | null;
  companyMission?: boolean | null;
  customerService?: boolean | null;
  subscriptionService?: boolean | null;
  packaging?: boolean | null;
  eatsTheFood?: boolean | null;
  cost?: boolean | null;
  healthBenefits?: boolean | null;
}

export interface SurveyResult {
  score?: number;
  reason?: string;
  improvements?: string;
  categoryScoring?: NpsCategoryScoring;
  postKatKinFood?: string;
}

interface CancellationReasonData {
  primaryReason: CancellationReason;
  secondaryReason?: CancellationReason;
  freeText?: string;
}

export interface UserId {
  authUserId?: string;
  anonymousId?: string;
  accessToken?: string;
  firstName?: string;
  lastName?: string;
}
